import { Flex, Spinner } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { Suspense, useEffect } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Smartlook from 'smartlook-client';

import { envConfig } from 'config';
import { useAuth, useHasSettings } from 'features/auth';
import { setCustomerInfoCookies } from 'features/auth/utils/setCustomerInfoCookies';
import { useFetchCurrentLazyQuery } from 'generated/graphql';
import { ALLOWED_PATHS } from 'globalConstants/leaAllowedPath';
import { TENANT_ID } from 'globalConstants/tenants';
import { FETCH_CURRENT } from 'javascript/actions/types';
import { ENVIRONMENT } from 'types/constants';
import { FEATURE_FLAG } from 'types/featureFlag';
import { RootStateType } from 'types/store';
import { mixpanelIdentify, mixpanelPeopleSetOnce, mixpanelRegister } from 'utils/mixpanel/mixpanel';

import { CurentDataType, useUserPermissions } from './useUserPermissions';

const LEM_URL = process.env.REACT_APP_LEM_URL;

export const AuthGuard: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setUserPermissions } = useUserPermissions();
  const [isLemV2Enabled] = useHasSettings(FEATURE_FLAG.LEM_V2_FEATURE);
  const [fetchCurrent, { data: currentData, loading }] = useFetchCurrentLazyQuery();
  const [tenantSlug] = useSelector(
    (state: RootStateType) => [state?.current?.tenant?.slug],
    shallowEqual
  );

  const isAllowedPath =
    ALLOWED_PATHS.includes(location.pathname) ||
    location.pathname.startsWith('/customers/') ||
    location.pathname.startsWith('/lea/') ||
    location.pathname.startsWith('/customer_asset') ||
    location.pathname.startsWith('/self_customer');

  useEffect(() => {
    if (!auth.isAuthenticated) {
      //enable login url in production
      if (envConfig.environment === ENVIRONMENT.PRODUCTION) {
        window.location.href = process.env.REACT_APP_REDIRECT_URL || '/';
      } else {
        navigate(`/auth/login?redirect=${location.pathname}`);
      }
    }
    if (auth.isAuthenticated && auth.isOnlyLeaOrBdr) {
      if (isLemV2Enabled && LEM_URL) {
        globalThis.location.href = LEM_URL;
      } else if (!isAllowedPath) {
        navigate('/lea');
      }
    }
  }, [
    auth.isAuthenticated,
    location.pathname,
    navigate,
    auth.isOnlyLeaOrBdr,
    isAllowedPath,
    isLemV2Enabled,
  ]);

  useEffect(() => {
    if (currentData) {
      if (!currentData?.current?.user) {
        Sentry.setUser({});
        return;
      }

      const {
        user: { email, name, phone, id: userId },
        customer,
      } = currentData.current;

      mixpanelPeopleSetOnce({
        $email: email,
        $name: name,
        userPhoneNumber: phone,
        tenantName: customer?.name ?? '',
        tenantId: customer?.id ?? '',
      });
      mixpanelIdentify(email);
      const mixpanelData = {
        tenantName: customer?.name,
        tenantId: customer?.id,
        tenantSlug: customer?.tenantSlug,
        username: name,
        userPhoneNumber: phone,
      };
      mixpanelRegister(mixpanelData);

      const timezone =
        currentData?.current?.settings?.find((item) => item.name === 'timezone')?.value || '';

      if (customer?.id === TENANT_ID.FUEL_PANDA) {
        Smartlook.disable(); //disable recording for fuelpanda tenant
      } else {
        Smartlook.identify(userId, {
          email: email ?? '',
          name: name ?? '',
          userPhoneNumber: phone ?? '',
          tenantName: customer?.name ?? '',
          tenantId: customer?.id ?? '',
        });
      }

      Sentry.setUser({
        email,
        name,
        timezone,
        tenantName: customer?.name,
        tenantId: customer?.id,
        tenantSlug: customer?.tenantSlug,
      });
      setCustomerInfoCookies(currentData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  useEffect(() => {
    if (!tenantSlug) {
      fetchCurrent();
    }
  }, [fetchCurrent, tenantSlug]);

  useEffect(() => {
    if (currentData && !loading) {
      dispatch({
        type: FETCH_CURRENT,
        payload: currentData.current,
      });
    }
  }, [currentData, dispatch, loading]);

  /**
   * User Permissions Setter for the whole app
   */
  useEffect(() => {
    if (!setUserPermissions) return;

    if (currentData?.current?.user?.id && !loading) {
      const data = currentData?.current as unknown as CurentDataType; // HACK

      setUserPermissions(data);
    }
  }, [currentData, dispatch, loading, setUserPermissions]);

  if (auth.isAuthenticated && auth.isOnlyLeaOrBdr && isLemV2Enabled && LEM_URL) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Suspense
      fallback={
        <Flex justify="center" align="center" h="100vh">
          <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
        </Flex>
      }
    >
      <Outlet />
    </Suspense>
  );
};
